import React, { useState, useEffect, useMemo } from 'react';
import { Building, FolderOpen, Users, Plus, XCircle } from 'lucide-react';
import AdminUsers from './AdminUsers';
import { organizationsApi, projectsApi, evaluationFrameworksApi, meApi } from '../services/api';
import { Organization, EvaluationFramework } from '../types';
import { useAuth } from '../auth/AuthContext';

type AdminTab = 'organizations' | 'projects' | 'users';

interface ConfigAdminProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const allTabs: { id: AdminTab; name: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'organizations', name: 'Organizations', icon: Building },
  { id: 'projects', name: 'Projects', icon: FolderOpen },
  { id: 'users', name: 'Users', icon: Users }
];

const ConfigAdmin: React.FC<ConfigAdminProps> = ({ addNotification }) => {
  const { roles, isMasterAdmin } = useAuth();
  /** Backend + /api/me may grant ADMIN without Firebase custom claims. */
  const isAdmin = roles.some((r) => r.toUpperCase() === 'ADMIN');
  /** Organizations CRUD uses require_admin — any admin may manage orgs, not only Firebase master_admin. */
  const canManageOrganizations = isMasterAdmin || isAdmin;

  const visibleTabs = useMemo(
    () => (canManageOrganizations ? allTabs : allTabs.filter((t) => t.id !== 'organizations')),
    [canManageOrganizations]
  );

  const [activeTab, setActiveTab] = useState<AdminTab>('organizations');

  useEffect(() => {
    const allowed = visibleTabs.some((t) => t.id === activeTab);
    if (!allowed && visibleTabs.length > 0) {
      setActiveTab(visibleTabs[0].id);
    }
  }, [canManageOrganizations, activeTab, visibleTabs]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100">Projects</h1>
        <p className="mt-1 text-sm text-slate-400">Manage organizations, projects, and users.</p>
      </div>

      <nav className="border-b border-slate-700">
        <div className="flex space-x-8">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-indigo-500 text-indigo-600'
                    : 'border-transparent text-slate-400 hover:text-slate-200 hover:border-slate-600'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.name}
              </button>
            );
          })}
        </div>
      </nav>

      <div className="min-h-[400px]">
        {activeTab === 'organizations' && canManageOrganizations && (
          <AdminOrganizationsTab addNotification={addNotification} />
        )}
        {activeTab === 'projects' && (
          <AdminProjectsTab addNotification={addNotification} />
        )}
        {activeTab === 'users' && (
          <div className="rounded-lg border border-slate-700 bg-slate-800 p-4">
            <AdminUsers addNotification={addNotification} />
          </div>
        )}
      </div>
    </div>
  );
};

// Organizations tab: list + add/edit/delete
interface AdminOrganizationsTabProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

const defaultFormData = () => ({ name: '', description: '', status: 'active' as string });

const AdminOrganizationsTab: React.FC<AdminOrganizationsTabProps> = ({ addNotification }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState(defaultFormData());
  const [submitting, setSubmitting] = useState(false);
  const [deletingOrg, setDeletingOrg] = useState<Organization | null>(null);

  const loadOrgs = () => {
    setLoading(true);
    organizationsApi.list().then((data) => {
      setOrganizations(data);
    }).catch(() => {
      addNotification('Failed to load organizations', 'error');
    }).finally(() => setLoading(false));
  };

  useEffect(() => { loadOrgs(); }, []);

  const openCreate = () => {
    setEditingOrg(null);
    setFormData(defaultFormData());
    setShowModal(true);
  };

  const openEdit = (org: Organization) => {
    setEditingOrg(org);
    setFormData({ name: org.name, description: org.description || '', status: org.status });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      addNotification('Organization name is required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (editingOrg) {
        await organizationsApi.update(editingOrg.id, formData);
        addNotification('Organization updated', 'success');
      } else {
        await organizationsApi.create(formData);
        addNotification('Organization created', 'success');
      }
      setShowModal(false);
      loadOrgs();
    } catch (err: any) {
      addNotification(err.response?.data?.detail || 'Failed to save', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingOrg) return;
    try {
      await organizationsApi.delete(deletingOrg.id);
      addNotification('Organization deleted', 'success');
      setDeletingOrg(null);
      loadOrgs();
    } catch {
      addNotification('Failed to delete organization', 'error');
    }
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-slate-100">Organizations</h2>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add organization
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-slate-400">Loading...</p>
      ) : organizations.length === 0 ? (
        <p className="text-sm text-slate-400">No organizations. Click Add organization to create one.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-900 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {organizations.map((org) => (
                <tr key={org.id}>
                  <td className="px-4 py-3 text-sm text-slate-100">{org.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{org.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{org.status}</td>
                  <td className="px-4 py-3 text-right text-sm">
                    <button onClick={() => openEdit(org)} className="text-indigo-600 hover:text-indigo-900 mr-4">Edit</button>
                    <button onClick={() => setDeletingOrg(org)} className="text-red-600 hover:text-red-900">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-slate-800">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-slate-100">{editingOrg ? 'Edit organization' : 'New organization'}</h3>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-200">
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="e.g., Acme Corp"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Optional"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-200 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData((p) => ({ ...p, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="suspended">Suspended</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700">Cancel</button>
              <button onClick={handleSave} disabled={submitting || !formData.name.trim()} className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50">
                {submitting ? 'Saving...' : editingOrg ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deletingOrg && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg shadow-lg p-6 max-w-sm w-full mx-4">
            <p className="text-slate-100 font-medium">Delete organization &quot;{deletingOrg.name}&quot;?</p>
            <p className="text-sm text-slate-400 mt-1">This cannot be undone.</p>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setDeletingOrg(null)} className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700">Cancel</button>
              <button onClick={handleDelete} className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700">Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Projects tab: org selector + project list with CRUD
interface AdminProjectsTabProps {
  addNotification: (message: string, type: 'success' | 'error' | 'info' | 'warning') => void;
}

type ProjectRow = { id: number; name: string; description?: string; status: string; evaluation_framework_id?: number | null };

const AdminProjectsTab: React.FC<AdminProjectsTabProps> = ({ addNotification }) => {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [loadingOrgs, setLoadingOrgs] = useState(true);
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [evaluationFrameworks, setEvaluationFrameworks] = useState<EvaluationFramework[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingProject, setEditingProject] = useState<{ id?: number; name: string; description?: string; status?: string; evaluation_framework_id?: number | null } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** User's default project id (only relevant when it belongs to selectedOrgId). */
  const [defaultProjectId, setDefaultProjectId] = useState<number | null>(null);

  useEffect(() => {
    const load = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await organizationsApi.list();
        setOrganizations(orgs);
        if (orgs.length > 0 && !selectedOrgId) setSelectedOrgId(orgs[0].id);
      } catch (err) {
        addNotification('Failed to load organizations', 'error');
      } finally {
        setLoadingOrgs(false);
      }
    };
    load();
  }, []);

  const loadDefaultProject = () => {
    meApi.getContext().then((ctx) => {
      setDefaultProjectId(ctx.default_project?.id ?? null);
    }).catch(() => setDefaultProjectId(null));
  };

  useEffect(() => {
    loadDefaultProject();
  }, [selectedOrgId]);

  useEffect(() => {
    const onMeContextChanged = () => loadDefaultProject();
    window.addEventListener('meContextChanged', onMeContextChanged);
    return () => window.removeEventListener('meContextChanged', onMeContextChanged);
  }, []);

  useEffect(() => {
    const load = async () => {
      const data = await evaluationFrameworksApi.getActiveFrameworks();
      setEvaluationFrameworks(data);
    };
    load();
  }, []);

  useEffect(() => {
    if (!selectedOrgId) {
      setProjects([]);
      return;
    }
    let cancelled = false;
    setLoadingProjects(true);
    projectsApi
      .list(selectedOrgId)
      .then((data) => {
        if (!cancelled) {
          setProjects(data.map((p) => ({ id: p.id, name: p.name, description: p.description, status: p.status, evaluation_framework_id: p.evaluation_framework_id })));
        }
      })
      .catch(() => {
        if (!cancelled) addNotification('Failed to load projects', 'error');
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });
    return () => { cancelled = true; };
  }, [selectedOrgId]);

  const loadProjects = () => {
    if (selectedOrgId) {
      projectsApi.list(selectedOrgId).then((data) => {
        setProjects(data.map((p) => ({ id: p.id, name: p.name, description: p.description, status: p.status, evaluation_framework_id: p.evaluation_framework_id })));
      });
    }
  };

  const handleSave = async () => {
    if (!selectedOrgId || !editingProject?.name?.trim()) {
      addNotification('Project name is required', 'error');
      return;
    }
    setSubmitting(true);
    try {
      if (editingProject.id) {
        await projectsApi.update(selectedOrgId, editingProject.id, {
          name: editingProject.name,
          description: editingProject.description,
          status: editingProject.status || 'active',
          evaluation_framework_id: editingProject.evaluation_framework_id ?? undefined
        });
        addNotification('Project updated successfully', 'success');
      } else {
        await projectsApi.create(selectedOrgId, {
          name: editingProject.name,
          description: editingProject.description,
          status: editingProject.status || 'active',
          evaluation_framework_id: editingProject.evaluation_framework_id ?? undefined
        } as any);
        addNotification('Project created successfully', 'success');
      }
      setShowModal(false);
      setEditingProject(null);
      loadProjects();
    } catch (err: any) {
      const msg = err.response?.data?.detail || 'Failed to save project';
      addNotification(msg, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (projectId: number) => {
    if (!selectedOrgId) return;
    if (!window.confirm('Delete this project?')) return;
    try {
      await projectsApi.delete(selectedOrgId, projectId);
      addNotification('Project deleted', 'success');
      loadProjects();
    } catch {
      addNotification('Failed to delete project', 'error');
    }
  };

  const renderProjectModal = () => {
    if (!showModal) return null;
    return (
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-5 border w-full max-w-lg shadow-lg rounded-md bg-slate-800">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-medium text-slate-100">{editingProject?.id ? 'Edit Project' : 'Create Project'}</h3>
            <button onClick={() => { setShowModal(false); setEditingProject(null); }} className="text-slate-400 hover:text-slate-200">
              <XCircle className="h-6 w-6" />
            </button>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Project Name *</label>
              <input
                type="text"
                value={editingProject?.name || ''}
                onChange={(e) => setEditingProject((prev) => ({ ...(prev || { name: '' }), name: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
                placeholder="e.g., Web App"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Description</label>
              <textarea
                value={editingProject?.description || ''}
                onChange={(e) => setEditingProject((prev) => ({ ...(prev || { name: '' }), description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
                placeholder="Optional"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Status</label>
              <select
                value={editingProject?.status ?? 'active'}
                onChange={(e) => setEditingProject((prev) => ({ ...(prev || { name: '' }), status: e.target.value }))}
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-200 mb-1">Evaluation Framework</label>
              <select
                value={editingProject?.evaluation_framework_id ?? ''}
                onChange={(e) =>
                  setEditingProject((prev) => ({
                    ...(prev || { name: '' }),
                    evaluation_framework_id: e.target.value ? parseInt(e.target.value) : null
                  }))
                }
                className="w-full px-3 py-2 border border-slate-600 rounded-md focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="">No Framework (Optional)</option>
                {evaluationFrameworks.map((f: EvaluationFramework) => (
                  <option key={f.id} value={f.id}>{f.name} ({f.framework_type})</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => { setShowModal(false); setEditingProject(null); }}
              className="px-4 py-2 text-sm font-medium text-slate-200 bg-slate-800 border border-slate-600 rounded-md hover:bg-slate-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={submitting || !editingProject?.name?.trim()}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting ? 'Saving...' : editingProject?.id ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-lg border border-slate-700 bg-slate-800 p-6">
      <div className="flex flex-wrap items-center gap-4 mb-6">
        <label className="text-sm font-medium text-slate-200">Organization</label>
        <select
          value={selectedOrgId ?? ''}
          onChange={(e) => setSelectedOrgId(e.target.value ? parseInt(e.target.value) : null)}
          className="rounded-md border border-slate-600 shadow-sm focus:border-purple-500 focus:ring-purple-500 sm:text-sm"
        >
          <option value="">Select organization</option>
          {organizations.map((org) => (
            <option key={org.id} value={org.id}>{org.name}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={() => { setEditingProject({ name: '', status: 'active' }); setShowModal(true); }}
          disabled={!selectedOrgId || loadingProjects}
          className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Project
        </button>
      </div>

      {loadingOrgs ? (
        <p className="text-sm text-slate-400">Loading organizations...</p>
      ) : !selectedOrgId ? (
        <p className="text-sm text-slate-400">Select an organization to manage projects.</p>
      ) : loadingProjects ? (
        <p className="text-sm text-slate-400">Loading projects...</p>
      ) : projects.length === 0 ? (
        <p className="text-sm text-slate-400">No projects. Click Add Project to create one.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-700">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Name</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Description</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Status</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-900 uppercase">Framework</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-900 uppercase">Default</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-900 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-slate-800 divide-y divide-slate-700">
              {projects.map((p) => (
                <tr key={p.id}>
                  <td className="px-4 py-3 text-sm text-slate-100">{p.name}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{p.description || '—'}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{p.status || 'active'}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">
                    {p.evaluation_framework_id
                      ? evaluationFrameworks.find((f: EvaluationFramework) => f.id === p.evaluation_framework_id)?.name ?? `ID ${p.evaluation_framework_id}`
                      : '—'}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {defaultProjectId === p.id ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                        Default project
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3 text-right text-sm space-x-2">
                    <button
                      onClick={async () => {
                        if (!selectedOrgId) return;
                        try {
                          await meApi.setDefaultProject(p.id);
                          setDefaultProjectId(p.id);
                          addNotification(`"${p.name}" set as default project`, 'success');
                          window.dispatchEvent(new Event('meContextChanged'));
                        } catch (err: any) {
                          addNotification(err.response?.data?.detail || 'Failed to set default project', 'error');
                        }
                      }}
                      className="text-slate-400 hover:text-slate-100"
                    >
                      Set default
                    </button>
                    <button
                      onClick={() => { setEditingProject({ id: p.id, name: p.name, description: p.description, status: p.status, evaluation_framework_id: p.evaluation_framework_id }); setShowModal(true); }}
                      className="text-indigo-600 hover:text-indigo-900"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(p.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {renderProjectModal()}
    </div>
  );
};

export default ConfigAdmin;
